import objTealium from 'src/utils/Tealium/Tealium';
import { OnboardingDocumentTypeEnum } from '../types/upload-documents';
import {
  EVENT_NAME,
  FORM_NAME,
  JOURNEY_NAME,
  JOURNEY_STEP_NAME,
  JOURNEY_TYPE,
  Link,
  LINK_NAME,
  LINK_PREFIX,
  PAGE_CHANNEL,
  PAGE_NAME,
  PAGE_NAME_PREFIX,
  PAGE_SECTION,
  PAGE_SUB_DOMAIN,
  PAGE_TYPE,
  StaticVariables,
  View,
  VISITOR_CUSTOMER_TYPE,
} from 'src/features/common/types/tealium-service';

const staticOnboardingTealiumVariables: StaticVariables = {
  page_url: window.location.href,
  page_section: PAGE_SECTION.SELF_SERVICE,
  page_type: PAGE_TYPE.WEBSITE,
  page_subdomain: PAGE_SUB_DOMAIN.SELF_SERVICE,
  page_channel: PAGE_CHANNEL.SELF_SERVICE,
  visitor_customer_type: VISITOR_CUSTOMER_TYPE.CONSUMER,
};

const staticToPVariables: StaticVariables = {
  page_url: window.location.href,
  page_section: PAGE_SECTION.TAP_ON_PHONE,
  page_channel: PAGE_CHANNEL.TAP_ON_PHONE,
  page_type: PAGE_TYPE.WEBVIEW,
  page_subdomain: PAGE_SUB_DOMAIN.TAP_ON_PHONE,
  visitor_customer_type: VISITOR_CUSTOMER_TYPE.CONSUMER,
};

const documentMap = new Map([
  [OnboardingDocumentTypeEnum.ID_BOOK, 'sa id'],
  [OnboardingDocumentTypeEnum.ID_CARD, 'sa id'],
  [OnboardingDocumentTypeEnum.ID_TYPE, 'sa id'],
  [OnboardingDocumentTypeEnum.PASSPORT, 'passport'],
  [OnboardingDocumentTypeEnum.BANK_PROOF, 'bank statement'],
]);

type Journey = 'pending app' | 'onboarding' | 'third party' | 'register device';

const journeyMap = new Map([
  ['pending app', JOURNEY_NAME.PENDING_APPLICATION],
  ['onboarding', JOURNEY_NAME.MERCHANT_ONBOARDING],
  ['third party', JOURNEY_NAME.THIRD_PARTY],
  ['register device', JOURNEY_NAME.DEVICE_ACTIVATION],
]);

const attempt = (func: () => void) => {
  try {
    func();
  } catch (err) {
    console.error(err);
    // intentional swallowing of non functional error
  }
};

const sendViewToTealium = (obj: View) => {
  const viewObj = {
    ...obj,
    event_name: obj.event_name.join(', '),
  };
  attempt(() => {
    objTealium.view(viewObj);
  });
};

const sendLinkToTealium = (obj: Link) => {
  const linkObj = {
    ...obj,
    event_name: obj.event_name.join(', '),
  };
  attempt(() => {
    objTealium.link(linkObj);
  });
};

const OnboardingTealiumService = {
  /** ##########################
   *   Pending Applications
   * ###########################
   */

  onUpdatePendingApplicationStart(): void {
    sendViewToTealium({
      event_name: [EVENT_NAME.PAGE_VIEW],
      page_name: `${PAGE_NAME_PREFIX.ONBOARDING}${PAGE_NAME.UPDATE_PENDING_APPLICATION}`,
      journey_name: JOURNEY_NAME.PENDING_APPLICATION,
      ...staticOnboardingTealiumVariables,
    });
  },

  onErrorToolTipActions(
    stepName: string,
    action: 'click' | 'contact number' | 'next' | 'previous',
    pageName: PAGE_NAME,
  ): void {
    const linkMap = new Map([
      ['click', LINK_NAME.ERROR_TOOL_TIP],
      ['contact number', LINK_NAME.ERROR_TOOL_TIP_CONTACT],
      ['next', LINK_NAME.ERROR_TOOL_TIP_NEXT],
      ['previous', LINK_NAME.ERROR_TOOL_TIP_PREVIOUS],
    ]);

    sendLinkToTealium({
      event_name: [EVENT_NAME.UI_INTERACTION],
      link_page_name: `${PAGE_NAME_PREFIX.ONBOARDING}${pageName}`,
      journey_name: JOURNEY_NAME.PENDING_APPLICATION,
      // @ts-ignore
      form_step_name: stepName,
      link_id: linkMap.get(action),
      ...staticOnboardingTealiumVariables,
    });
  },

  /** ##########################
   *   Setup Your Account
   * ###########################
   */
  onSetupYourAccountStart: (journey: Journey) => {
    sendViewToTealium({
      event_name: [EVENT_NAME.PAGE_VIEW],
      page_name: `${PAGE_NAME_PREFIX.ONBOARDING}${PAGE_NAME.SETUP_YOUR_ACCOUNT}`,
      journey_name: journeyMap.get(journey)!,
      ...staticOnboardingTealiumVariables,
    });
  },

  onSetupYourAccountAction: (journey: Journey) => {
    journey === 'pending app'
      ? sendLinkToTealium({
          event_name: [EVENT_NAME.UI_INTERACTION, EVENT_NAME.FORM_START, EVENT_NAME.JOURNEY_START],
          link_page_name: `${PAGE_NAME_PREFIX.ONBOARDING}${PAGE_NAME.UPDATE_PENDING_APPLICATION}`,
          journey_name: JOURNEY_NAME.PENDING_APPLICATION,
          ...staticOnboardingTealiumVariables,
        })
      : sendLinkToTealium({
          event_name: [EVENT_NAME.UI_INTERACTION, EVENT_NAME.JOURNEY_START],
          link_page_name: `${PAGE_NAME_PREFIX.ONBOARDING}${PAGE_NAME.SETUP_YOUR_ACCOUNT}`,
          journey_name: journeyMap.get(journey)!,
          link_name: LINK_NAME.GET_STARTED,
          ...staticOnboardingTealiumVariables,
        });
  },

  /** ##########################
   *   Webcam
   * ###########################
   */

  onWebCamDisabledToPStart: () => {
    sendViewToTealium({
      event_name: [EVENT_NAME.PAGE_VIEW],
      page_name: `${PAGE_NAME_PREFIX.TOP_ONBOARDING}${PAGE_NAME.SELFIE_CAPTURE_WEBCAM}`,
      journey_name: JOURNEY_NAME.TAP_ON_PHONE,
      ...staticToPVariables,
    });
  },

  onWebCamDisabledToPAction: (action: "i'll be back" | "i've enabled it") => {
    const map = new Map([
      ["i'll be back", LINK_NAME.SELFIE_CAPTURE_WEBCAM_BACK],
      ["i've enabled it", LINK_NAME.SELFIE_CAPTURE_WEBCAM_ENABLED],
    ]);
    sendLinkToTealium({
      event_name: [EVENT_NAME.UI_INTERACTION],
      link_page_name: `${PAGE_NAME_PREFIX.TOP_ONBOARDING}${PAGE_NAME.SELFIE_CAPTURE_WEBCAM}`,
      journey_name: JOURNEY_NAME.TAP_ON_PHONE,
      link_id: `${LINK_PREFIX.TOP_ONBOARDING}${map.get(action)!}`,
      ...staticToPVariables,
    });
  },

  /** ##########################
   *   Company/Business Details
   * ###########################
   */

  onCompanyDetailsStart: (journey: Journey) => {
    sendViewToTealium({
      event_name: [EVENT_NAME.PAGE_VIEW, EVENT_NAME.JOURNEY_START, EVENT_NAME.FORM_START],
      page_name: `${PAGE_NAME_PREFIX.ONBOARDING}${PAGE_NAME.ABOUT_YOUR_BUSINESS}`,
      form_name: FORM_NAME.ABOUT_YOUR_BUSINESS,
      journey_name: journeyMap.get(journey)!,
      ...staticOnboardingTealiumVariables,
    });
  },

  onCompanyDetailsAction: (journey: Journey) => {
    sendLinkToTealium({
      event_name: [EVENT_NAME.PAGE_VIEW, EVENT_NAME.FORM_START, EVENT_NAME.JOURNEY_START],
      link_page_name: `${PAGE_NAME_PREFIX.ONBOARDING}${PAGE_NAME.ABOUT_YOUR_BUSINESS}`,
      journey_name: journeyMap.get(journey)!,
      form_name: FORM_NAME.ABOUT_YOUR_BUSINESS,
      ...staticOnboardingTealiumVariables,
    });
  },

  onCompanyDetailsTooltipClickAction: (journey: Journey) => {
    sendLinkToTealium({
      event_name: [EVENT_NAME.UI_INTERACTION],
      link_page_name: `${PAGE_NAME_PREFIX.ONBOARDING}${PAGE_NAME.ABOUT_YOUR_BUSINESS}`,
      journey_name: journeyMap.get(journey)!,
      link_id: `${LINK_PREFIX.SELF_SERVICE}${LINK_NAME.TOOL_TIP}`,
      form_name: FORM_NAME.ABOUT_YOUR_BUSINESS,
      ...staticOnboardingTealiumVariables,
    });
  },

  onCompanyDetailsEnd: (journey: Journey) => {
    sendLinkToTealium({
      event_name: [EVENT_NAME.UI_INTERACTION, EVENT_NAME.FORM_END],
      link_page_name: `${PAGE_NAME_PREFIX.ONBOARDING}${PAGE_NAME.ABOUT_YOUR_BUSINESS}`,
      journey_name: journeyMap.get(journey)!,
      form_name: FORM_NAME.ABOUT_YOUR_BUSINESS,
      link_id: LINK_NAME.NEXT,
      ...staticOnboardingTealiumVariables,
    });
  },

  onBackBusinessDetails: (
    completionRate: string,
    incompleteFields: string,
    productName?: string,
  ) => {
    sendLinkToTealium({
      event_name: [EVENT_NAME.UI_INTERACTION],
      form_name: FORM_NAME.ABOUT_YOUR_BUSINESS,
      link_page_name: `${PAGE_NAME_PREFIX.ONBOARDING}${PAGE_NAME.ABOUT_YOUR_BUSINESS}`,
      link_name: `${LINK_PREFIX.SELF_SERVICE}${LINK_NAME.ABOUT_BUSINESS}`,
      journey_name: JOURNEY_NAME.MERCHANT_ONBOARDING,
      form_completion_rate: completionRate,
      form_field_incomplete: incompleteFields,
      product_name: productName,
      ...staticOnboardingTealiumVariables,
    });
  },

  onCompanyDetailsToPStart: () => {
    sendViewToTealium({
      event_name: [EVENT_NAME.PAGE_VIEW, EVENT_NAME.JOURNEY_STEP],
      page_name: `${PAGE_NAME_PREFIX.TOP_ONBOARDING}${PAGE_NAME.COMPANY_DETAILS}`,
      journey_name: JOURNEY_NAME.TAP_ON_PHONE,
      journey_type: JOURNEY_TYPE.ONBOARDING,
      journey_step_name: JOURNEY_STEP_NAME.COMPANY_DETAILS,
      ...staticToPVariables,
    });
  },

  onCompanyDetailsToPFormStart: () => {
    sendViewToTealium({
      event_name: [EVENT_NAME.FORM_START],
      page_name: `${PAGE_NAME_PREFIX.TOP_ONBOARDING}${PAGE_NAME.COMPANY_DETAILS}`,
      journey_name: JOURNEY_NAME.TAP_ON_PHONE,
      form_name: FORM_NAME.COMPANY_DETAILS,
      ...staticToPVariables,
    });
  },

  onCompanyDetailsToPIdle: (incompleteFields: string, formCompletionRate: string) => {
    sendViewToTealium({
      event_name: [EVENT_NAME.PAGE_VIEW],
      page_name: `${PAGE_NAME_PREFIX.TOP_ONBOARDING}${PAGE_NAME.COMPANY_DETAILS}`,
      journey_name: JOURNEY_NAME.TAP_ON_PHONE,
      form_name: FORM_NAME.COMPANY_DETAILS,
      form_completion_rate: `${formCompletionRate}%`,
      form_field_incomplete: incompleteFields === '' ? undefined : incompleteFields,
      ...staticToPVariables,
    });
  },

  onCompanyDetailsToPAction: (checkBox: string, enabled: boolean) => {
    sendLinkToTealium({
      event_name: [EVENT_NAME.UI_INTERACTION],
      link_page_name: `${PAGE_NAME_PREFIX.TOP_ONBOARDING}${PAGE_NAME.COMPANY_DETAILS}`,
      link_id: `${LINK_PREFIX.TOP_ONBOARDING}${LINK_NAME.COMPANY_DETAILS}: ${checkBox}: ${
        enabled ? 'enabled' : 'disabled'
      }`,
      journey_name: JOURNEY_NAME.TAP_ON_PHONE,
      ...staticToPVariables,
    });
  },

  onCompanyDetailsToPEnd: () => {
    sendLinkToTealium({
      event_name: [EVENT_NAME.UI_INTERACTION, EVENT_NAME.FORM_END],
      link_page_name: `${PAGE_NAME_PREFIX.TOP_ONBOARDING}${PAGE_NAME.COMPANY_DETAILS}`,
      journey_name: JOURNEY_NAME.TAP_ON_PHONE,
      form_name: FORM_NAME.COMPANY_DETAILS,
      link_id: `${LINK_PREFIX.TOP_ONBOARDING}${LINK_NAME.COMPANY_DETAILS}: next`,
      ...staticToPVariables,
    });
  },

  /** ##########################
   *   Company/Business Address Details
   * ###########################
   */

  onConfirmCompanyAddressStart: (journey: Journey) => {
    sendViewToTealium({
      event_name: [EVENT_NAME.PAGE_VIEW, EVENT_NAME.FORM_START],
      page_name: `${PAGE_NAME_PREFIX.ONBOARDING}${PAGE_NAME.COMPANY_ADDRESS}`,
      form_name: FORM_NAME.COMPANY_ADDRESS,
      journey_name: journeyMap.get(journey)!,
      ...staticOnboardingTealiumVariables,
    });
  },

  onConfirmCompanyAddressEnd: (journey: Journey) => {
    sendLinkToTealium({
      event_name: [EVENT_NAME.UI_INTERACTION, EVENT_NAME.FORM_END],
      link_page_name: `${PAGE_NAME_PREFIX.ONBOARDING}${PAGE_NAME.COMPANY_ADDRESS}`,
      journey_name: journeyMap.get(journey)!,
      form_name: FORM_NAME.COMPANY_ADDRESS,
      link_id: journey === 'pending app' ? LINK_NAME.CONFIRM : LINK_NAME.NEXT,
      ...staticOnboardingTealiumVariables,
    });
  },

  /** ####################
   *   Director Details
   * ####################
   */

  onDirectorsDetailsStart: (journey: Journey) => {
    sendViewToTealium({
      event_name: [EVENT_NAME.PAGE_VIEW, EVENT_NAME.FORM_START],
      page_name: `${PAGE_NAME_PREFIX.TOP_ONBOARDING}${PAGE_NAME.ABOUT_YOUR_DIRECTORS}`,
      form_name: FORM_NAME.ABOUT_YOUR_DIRECTORS,
      journey_name: journeyMap.get(journey)!,
      ...staticOnboardingTealiumVariables,
    });
  },

  onDirectorsDetailsAction: (journey: Journey) => {
    sendLinkToTealium({
      event_name: [EVENT_NAME.UI_INTERACTION],
      link_page_name: `${PAGE_NAME_PREFIX.ONBOARDING}${PAGE_NAME.ABOUT_YOUR_DIRECTORS}`,
      journey_name: journeyMap.get(journey)!,
      link_id: LINK_NAME.ADD_DIRECTOR,
      ...staticOnboardingTealiumVariables,
    });
  },

  onDirectorsDetailsEnd: (citizenshipTypes: string[], journey: Journey) => {
    sendLinkToTealium({
      event_name: [EVENT_NAME.UI_INTERACTION, EVENT_NAME.FORM_END],
      link_page_name: `${PAGE_NAME_PREFIX.ONBOARDING}${PAGE_NAME.ABOUT_YOUR_DIRECTORS}`,
      journey_name: journeyMap.get(journey)!,
      form_name: FORM_NAME.ABOUT_YOUR_DIRECTORS,
      link_id: LINK_NAME.NEXT,
      citizenship_type: citizenshipTypes.join(','),
      ...staticOnboardingTealiumVariables,
    });
  },

  onDirectorsDetailsToPStart: () => {
    sendViewToTealium({
      event_name: [EVENT_NAME.PAGE_VIEW, EVENT_NAME.JOURNEY_STEP],
      page_name: `${PAGE_NAME_PREFIX.TOP_ONBOARDING}${PAGE_NAME.COMPANY_DIRECTOR_DETAILS}`,
      journey_name: JOURNEY_NAME.TAP_ON_PHONE,
      journey_type: JOURNEY_TYPE.ONBOARDING,
      journey_step_name: JOURNEY_STEP_NAME.DIRECTOR_INFORMATION,
      ...staticToPVariables,
    });
  },

  onDirectorsDetailsToPFormStart: () => {
    sendViewToTealium({
      event_name: [EVENT_NAME.FORM_START],
      page_name: `${PAGE_NAME_PREFIX.TOP_ONBOARDING}${PAGE_NAME.COMPANY_DIRECTOR_DETAILS}`,
      journey_name: JOURNEY_NAME.TAP_ON_PHONE,
      form_name: FORM_NAME.DIRECTOR_INFORMATION,
      ...staticToPVariables,
    });
  },

  onDirectorsDetailsToPEnd: () => {
    sendLinkToTealium({
      event_name: [EVENT_NAME.UI_INTERACTION, EVENT_NAME.FORM_END],
      link_page_name: `${PAGE_NAME_PREFIX.TOP_ONBOARDING}${PAGE_NAME.COMPANY_DIRECTOR_DETAILS}`,
      journey_name: JOURNEY_NAME.TAP_ON_PHONE,
      form_name: FORM_NAME.DIRECTOR_INFORMATION,
      link_id: `${LINK_PREFIX.TOP_ONBOARDING}${LINK_NAME.COMPANY_DIRECTOR_DETAILS}: next`,
      ...staticToPVariables,
    });
  },

  /** ####################
   *   Personal Details
   * ####################
   */

  onPersonalDetailsStart: (journey: Journey) => {
    sendViewToTealium({
      event_name: [EVENT_NAME.PAGE_VIEW, EVENT_NAME.FORM_START],
      page_name: `${PAGE_NAME_PREFIX.ONBOARDING}${PAGE_NAME.PERSONAL_DETAILS}`,
      form_name: FORM_NAME.PERSONAL_DETAILS,
      journey_name: journeyMap.get(journey)!,
      ...staticOnboardingTealiumVariables,
    });
  },

  onPersonalDetailsAction: () => {
    sendLinkToTealium({
      event_name: [EVENT_NAME.UI_INTERACTION, EVENT_NAME.FORM_END],
      link_page_name: `${PAGE_NAME_PREFIX.ONBOARDING}${PAGE_NAME.PERSONAL_DETAILS}`,
      journey_name: JOURNEY_NAME.MERCHANT_ONBOARDING,
      form_name: FORM_NAME.PERSONAL_DETAILS,
      ...staticOnboardingTealiumVariables,
    });
  },

  onPersonalDetailsToolTipClickActon: () => {
    sendLinkToTealium({
      event_name: [EVENT_NAME.UI_INTERACTION],
      link_page_name: `${PAGE_NAME_PREFIX.ONBOARDING}${PAGE_NAME.PERSONAL_DETAILS}`,
      journey_name: JOURNEY_NAME.MERCHANT_ONBOARDING,
      link_id: `${LINK_PREFIX.SELF_SERVICE}${LINK_NAME.TOOL_TIP}`,
      form_name: FORM_NAME.PERSONAL_DETAILS,
      ...staticOnboardingTealiumVariables,
    });
  },

  onPersonalDetailsEnd: (citizenshipType: string, journey: Journey) => {
    sendLinkToTealium({
      event_name: [EVENT_NAME.UI_INTERACTION, EVENT_NAME.FORM_END],
      link_page_name: `${PAGE_NAME_PREFIX.ONBOARDING}${PAGE_NAME.PERSONAL_DETAILS}`,
      journey_name: journeyMap.get(journey)!,
      form_name: FORM_NAME.PERSONAL_DETAILS,
      link_id: journey === 'pending app' ? LINK_NAME.CONFIRM : LINK_NAME.NEXT,
      citizenship_type: citizenshipType,
      ...staticOnboardingTealiumVariables,
    });
  },

  onPersonalDetailsToPStart: () => {
    sendViewToTealium({
      event_name: [EVENT_NAME.PAGE_VIEW, EVENT_NAME.FORM_START, EVENT_NAME.JOURNEY_STEP],
      page_name: `${PAGE_NAME_PREFIX.TOP_ONBOARDING}${PAGE_NAME.PERSONAL_DETAILS_CONFIRM}`,
      form_name: FORM_NAME.PERSONAL_DETAILS_CONFIRM,
      journey_name: JOURNEY_NAME.TAP_ON_PHONE,
      journey_type: JOURNEY_TYPE.ONBOARDING,
      journey_step_name: JOURNEY_STEP_NAME.PERSONAL_DETAILS_CONFIRM,
      ...staticToPVariables,
    });
  },

  onPersonalDetailsToPIdle: (incompleteFields: string, formCompletionRate: string) => {
    sendViewToTealium({
      event_name: [EVENT_NAME.PAGE_VIEW],
      page_name: `${PAGE_NAME_PREFIX.TOP_ONBOARDING}${PAGE_NAME.PERSONAL_DETAILS_CONFIRM}`,
      journey_name: JOURNEY_NAME.TAP_ON_PHONE,
      form_name: FORM_NAME.PERSONAL_DETAILS_CONFIRM,
      form_completion_rate: `${formCompletionRate}%`,
      form_field_incomplete: incompleteFields,
      ...staticToPVariables,
    });
  },

  onPersonalDetailsToPEnd: () => {
    sendLinkToTealium({
      event_name: [EVENT_NAME.UI_INTERACTION, EVENT_NAME.FORM_END],
      link_page_name: `${PAGE_NAME_PREFIX.TOP_ONBOARDING}${PAGE_NAME.PERSONAL_DETAILS_CONFIRM}`,
      journey_name: JOURNEY_NAME.TAP_ON_PHONE,
      form_name: FORM_NAME.PERSONAL_DETAILS_CONFIRM,
      link_id: `${LINK_PREFIX.TOP_ONBOARDING}${LINK_NAME.PERSONAL_DETAILS_CONFIRM}: next`,
      ...staticToPVariables,
    });
  },

  /** ####################
   *   Personal Selfie
   * ####################
   */

  onTakeASelfieStart: (journey: Journey) => {
    sendViewToTealium({
      event_name: [EVENT_NAME.PAGE_VIEW],
      page_name: `${PAGE_NAME_PREFIX.ONBOARDING}${PAGE_NAME.SELFIE_CAPTURE}`,
      journey_name: journeyMap.get(journey)!,
      ...staticOnboardingTealiumVariables,
    });
  },

  onTakeASelfieAction: (journey: Journey) => {
    sendLinkToTealium({
      event_name: [EVENT_NAME.UI_INTERACTION],
      link_page_name: `${PAGE_NAME_PREFIX.ONBOARDING}${PAGE_NAME.SELFIE_CAPTURE}`,
      journey_name: journeyMap.get(journey)!,
      link_id: `${LINK_PREFIX.SELF_SERVICE}${LINK_NAME.TAKE_PHOTO}`,
      ...staticOnboardingTealiumVariables,
    });
  },

  onTakeASelfieToPStart: () => {
    sendViewToTealium({
      event_name: [EVENT_NAME.PAGE_VIEW, EVENT_NAME.JOURNEY_STEP],
      page_name: `${PAGE_NAME_PREFIX.TOP_ONBOARDING}${PAGE_NAME.SELFIE_CAPTURE}`,
      journey_name: JOURNEY_NAME.TAP_ON_PHONE,
      journey_type: JOURNEY_TYPE.ONBOARDING,
      journey_step_name: JOURNEY_STEP_NAME.SELFIE_CAPTURE,
      ...staticToPVariables,
    });
  },

  onTakeASelfieToPOpenCamera: () => {
    sendLinkToTealium({
      event_name: [EVENT_NAME.UI_INTERACTION],
      link_page_name: `${PAGE_NAME_PREFIX.TOP_ONBOARDING}${PAGE_NAME.SELFIE_CAPTURE}`,
      journey_name: JOURNEY_NAME.TAP_ON_PHONE,
      link_id: `${LINK_PREFIX.TOP_ONBOARDING}${LINK_NAME.SELFIE_CAPTURE_OPEN_CAMERA}`,
      ...staticToPVariables,
    });
  },

  onTakeASelfieToPModalStart: () => {
    sendViewToTealium({
      event_name: [EVENT_NAME.PAGE_VIEW, EVENT_NAME.JOURNEY_STEP],
      page_name: `${PAGE_NAME_PREFIX.TOP_ONBOARDING}${PAGE_NAME.SELFIE_SCREEN}`,
      journey_name: JOURNEY_NAME.TAP_ON_PHONE,
      journey_type: JOURNEY_TYPE.ONBOARDING,
      journey_step_name: JOURNEY_STEP_NAME.TAKE_PHOTO,
      ...staticToPVariables,
    });
  },

  onTakeASelfieToPAction: () => {
    sendLinkToTealium({
      event_name: [EVENT_NAME.UI_INTERACTION],
      link_page_name: `${PAGE_NAME_PREFIX.TOP_ONBOARDING}${PAGE_NAME.SELFIE_SCREEN}`,
      journey_name: JOURNEY_NAME.TAP_ON_PHONE,
      link_id: `${LINK_PREFIX.TOP_ONBOARDING}${LINK_NAME.SELFIE_SCREEN_TAKE_PHOTO}`,
      ...staticToPVariables,
    });
  },

  onTakeASelfieToPEnd: () => {
    sendLinkToTealium({
      event_name: [EVENT_NAME.UI_INTERACTION],
      link_page_name: `${PAGE_NAME_PREFIX.TOP_ONBOARDING}${PAGE_NAME.SELFIE_CAPTURE}`,
      journey_name: JOURNEY_NAME.TAP_ON_PHONE,
      form_name: FORM_NAME.PERSONAL_DETAILS_CONFIRM,
      link_id: `${LINK_PREFIX.TOP_ONBOARDING}${LINK_NAME.SELFIE_CAPTURE_NEXT}`,
      ...staticToPVariables,
    });
  },

  /** ####################
   *   Declined Application
   * ####################
   */
  onOnboardingDeclinedStart: () => {
    sendLinkToTealium({
      event_name: [EVENT_NAME.PAGE_VIEW, EVENT_NAME.JOURNEY_END],
      link_page_name: `${PAGE_NAME_PREFIX.ONBOARDING}${PAGE_NAME.APPLICATION_DECLINED}`,
      journey_name: JOURNEY_NAME.PENDING_APPLICATION,
      ...staticOnboardingTealiumVariables,
    });
  },

  onOnboardingDeclinedAction: (action: 'call support' | 'email support') => {
    const linkMap = new Map([
      ['call support', LINK_NAME.UPDATE_PENDING_APPLICATION_CALL],
      ['email support', LINK_NAME.UPDATE_PENDING_APPLICATION_EMAIL],
    ]);
    sendLinkToTealium({
      event_name: [EVENT_NAME.UI_INTERACTION],
      link_page_name: `${PAGE_NAME_PREFIX.ONBOARDING}${PAGE_NAME.APPLICATION_DECLINED}`,
      link_id: linkMap.get(action),
      journey_name: JOURNEY_NAME.PENDING_APPLICATION,
      ...staticOnboardingTealiumVariables,
    });
  },

  onOnboardingDeclinedEnd: () => {
    sendLinkToTealium({
      event_name: [EVENT_NAME.PAGE_VIEW, EVENT_NAME.JOURNEY_END],
      link_page_name: `${PAGE_NAME_PREFIX.ONBOARDING}${PAGE_NAME.APPLICATION_DECLINED}`,
      link_id: LINK_NAME.UPDATE_PENDING_APPLICATION_EXIT,
      journey_name: JOURNEY_NAME.PENDING_APPLICATION,
      ...staticOnboardingTealiumVariables,
    });
  },

  /** ####################
   *   Residential Address
   * ####################
   */

  onResidentialAddressStart: (journey: Journey) => {
    sendViewToTealium({
      event_name: [EVENT_NAME.PAGE_VIEW, EVENT_NAME.FORM_START],
      page_name: `${PAGE_NAME_PREFIX.ONBOARDING}${PAGE_NAME.RESIDENTIAL_ADDRESS}`,
      form_name: FORM_NAME.RESIDENTIAL_ADDRESS,
      journey_name: journeyMap.get(journey)!,
      ...staticOnboardingTealiumVariables,
    });
  },

  onResidentialAddressEnd: (journey: Journey) => {
    sendLinkToTealium({
      event_name: [EVENT_NAME.UI_INTERACTION, EVENT_NAME.FORM_END],
      link_page_name: `${PAGE_NAME_PREFIX.ONBOARDING}${PAGE_NAME.RESIDENTIAL_ADDRESS}`,
      journey_name: journeyMap.get(journey)!,
      form_name: FORM_NAME.RESIDENTIAL_ADDRESS,
      link_id: LINK_NAME.NEXT,
      ...staticOnboardingTealiumVariables,
    });
  },

  /** ####################
   *   Banking Details
   * ####################
   */

  onBankDetailsStart: (journey: Journey) => {
    sendViewToTealium({
      event_name: [EVENT_NAME.PAGE_VIEW, EVENT_NAME.FORM_START],
      page_name: `${PAGE_NAME_PREFIX.ONBOARDING}${PAGE_NAME.BANK_DETAILS}`,
      form_name: FORM_NAME.BANK_DETAILS,
      journey_name: journeyMap.get(journey)!,
      ...staticOnboardingTealiumVariables,
    });
  },

  onBankDetailsVerificationFailedActions: (
    failureCount: number,
    eventErrorName: string,
    eventErrorCode: string,
    journey: Journey,
  ) => {
    sendLinkToTealium({
      event_name: [EVENT_NAME.UI_INTERACTION],
      link_page_name: `${PAGE_NAME_PREFIX.ONBOARDING}${PAGE_NAME.BANK_DETAILS}`,
      link_id: `${LINK_PREFIX.SELF_SERVICE}${LINK_NAME.BANK_DETAILS_VERIFY_FAILED}: ${
        failureCount ? `${failureCount}` : ''
      }`,
      journey_name: journeyMap.get(journey)!,
      event_error_name: eventErrorName,
      event_error_code: eventErrorCode,
      event_error_type: 'user error',
      ...staticOnboardingTealiumVariables,
    });
  },

  onBankDetailsEnd: (journey: Journey) => {
    sendLinkToTealium({
      event_name: [EVENT_NAME.UI_INTERACTION, EVENT_NAME.FORM_END],
      link_page_name: `${PAGE_NAME_PREFIX.ONBOARDING}${PAGE_NAME.BANK_DETAILS}`,
      form_name: FORM_NAME.BANK_DETAILS,
      journey_name: journeyMap.get(journey)!,
      ...staticOnboardingTealiumVariables,
    });
  },

  onBankDetailsToPStart: () => {
    sendViewToTealium({
      event_name: [EVENT_NAME.PAGE_VIEW, EVENT_NAME.JOURNEY_STEP],
      page_name: `${PAGE_NAME_PREFIX.TOP_ONBOARDING}${PAGE_NAME.BANK_DETAILS}`,
      journey_name: JOURNEY_NAME.TAP_ON_PHONE,
      journey_type: JOURNEY_TYPE.ONBOARDING,
      journey_step_name: JOURNEY_STEP_NAME.BANK_DETAILS,
      ...staticToPVariables,
    });
  },

  onBankDetailsToPFormStart: () => {
    sendViewToTealium({
      event_name: [EVENT_NAME.FORM_START],
      page_name: `${PAGE_NAME_PREFIX.TOP_ONBOARDING}${PAGE_NAME.BANK_DETAILS}`,
      journey_name: JOURNEY_NAME.TAP_ON_PHONE,
      form_name: FORM_NAME.BANKING_DETAILS,
      ...staticToPVariables,
    });
  },

  onBankDetailsToPIdle: (incompleteFields: string, formCompletionRate: string) => {
    sendViewToTealium({
      event_name: [EVENT_NAME.PAGE_VIEW],
      page_name: `${PAGE_NAME_PREFIX.TOP_ONBOARDING}${PAGE_NAME.BANK_DETAILS}`,
      journey_name: JOURNEY_NAME.TAP_ON_PHONE,
      form_name: FORM_NAME.BANKING_DETAILS,
      form_completion_rate: `${formCompletionRate}%`,
      form_field_incomplete: incompleteFields,
      ...staticToPVariables,
    });
  },

  onBankDetailsToPEnd: () => {
    sendLinkToTealium({
      event_name: [EVENT_NAME.UI_INTERACTION, EVENT_NAME.FORM_END],
      link_page_name: `${PAGE_NAME_PREFIX.TOP_ONBOARDING}${PAGE_NAME.BANK_DETAILS}`,
      form_name: FORM_NAME.BANKING_DETAILS,
      journey_name: JOURNEY_NAME.TAP_ON_PHONE,
      link_id: `${LINK_PREFIX.TOP_ONBOARDING}${LINK_NAME.BANK_DETAILS_NEXT}`,
      ...staticToPVariables,
    });
  },

  /** ####################
   *   Upload Documents
   * ####################
   */

  onDocumentUploadStart: (journey: Journey) => {
    sendViewToTealium({
      event_name: [EVENT_NAME.PAGE_VIEW],
      page_name: `${PAGE_NAME_PREFIX.ONBOARDING}${PAGE_NAME.UPLOAD_DOCUMENTS}`,
      journey_name: journeyMap.get(journey)!,
      ...staticOnboardingTealiumVariables,
    });
  },

  onDocumentUploadAction: (documentType: string, journey: Journey) => {
    sendLinkToTealium({
      event_name: [EVENT_NAME.UI_INTERACTION],
      link_page_name: `${PAGE_NAME_PREFIX.ONBOARDING}${PAGE_NAME.UPLOAD_DOCUMENTS}`,
      journey_name: journeyMap.get(journey)!,
      link_id: `${LINK_PREFIX.SELF_SERVICE}${LINK_NAME.DOCUMENT_UPLOADED}`,
      event_label: 'document type',
      event_action: documentType,
      ...staticOnboardingTealiumVariables,
    });
  },

  onDocumentUploadToPStart: () => {
    sendViewToTealium({
      event_name: [EVENT_NAME.PAGE_VIEW, EVENT_NAME.JOURNEY_STEP],
      page_name: `${PAGE_NAME_PREFIX.TOP_ONBOARDING}${PAGE_NAME.UPLOAD_DOCUMENTS}`,
      journey_name: JOURNEY_NAME.TAP_ON_PHONE,
      journey_type: JOURNEY_TYPE.ONBOARDING,
      journey_step_name: JOURNEY_STEP_NAME.UPLOAD_DOCUMENTS,
      ...staticToPVariables,
    });
  },

  onDocumentUploadToPAction: (documentType: OnboardingDocumentTypeEnum) => {
    const doc = documentMap.get(documentType);
    if (doc) {
      sendLinkToTealium({
        event_name: [EVENT_NAME.UI_INTERACTION],
        link_page_name: `${PAGE_NAME_PREFIX.TOP_ONBOARDING}${PAGE_NAME.UPLOAD_DOCUMENTS}`,
        journey_name: JOURNEY_NAME.TAP_ON_PHONE,
        link_id: `${LINK_PREFIX.TOP_ONBOARDING}${LINK_NAME.UPLOAD_DOCUMENTS}: ${doc}: upload`,
        ...staticToPVariables,
      });
    }
  },

  onDocumentUploadToPEnd: () => {
    sendLinkToTealium({
      event_name: [EVENT_NAME.UI_INTERACTION],
      link_page_name: `${PAGE_NAME_PREFIX.TOP_ONBOARDING}${PAGE_NAME.UPLOAD_DOCUMENTS}`,
      journey_name: JOURNEY_NAME.TAP_ON_PHONE,
      link_id: `${LINK_PREFIX.TOP_ONBOARDING}${LINK_NAME.UPLOAD_DOCUMENTS_NEXT}`,
      ...staticToPVariables,
    });
  },

  /** ####################
   *   Completed Journey
   * ####################
   */

  onCompletedJourneyStart: (journey: Journey) => {
    sendViewToTealium({
      event_name: [
        EVENT_NAME.PAGE_VIEW,
        EVENT_NAME.JOURNEY_END,
        EVENT_NAME.MERCHANT_ONBOARDING_COMPLETE,
      ],
      page_name: `${PAGE_NAME_PREFIX.ONBOARDING}${PAGE_NAME.ONBOARDING_COMPLETE}`,
      journey_name: journeyMap.get(journey)!,
      ...staticOnboardingTealiumVariables,
    });
  },

  onCompletedJourneyToPStart: () => {
    sendViewToTealium({
      event_name: [
        EVENT_NAME.PAGE_VIEW,
        EVENT_NAME.JOURNEY_END,
        EVENT_NAME.MERCHANT_ONBOARDING_COMPLETE,
      ],
      page_name: `${PAGE_NAME_PREFIX.TOP_ONBOARDING}${PAGE_NAME.ALMOST_THERE}`,
      journey_name: JOURNEY_NAME.TAP_ON_PHONE,
      journey_type: JOURNEY_TYPE.ONBOARDING,
      ...staticToPVariables,
    });
  },

  onExitConfirmationStart: () => {
    sendViewToTealium({
      event_name: [EVENT_NAME.PAGE_VIEW],
      page_name: `${PAGE_NAME_PREFIX.ONBOARDING}${PAGE_NAME.EXIT_MODAL}`,
      journey_name: JOURNEY_NAME.MERCHANT_ONBOARDING,
      ...staticOnboardingTealiumVariables,
    });
  },

  /** ####################
   *   Under Age Modal
   * ####################
   */
  onUnderageModalStart: (journey: Journey) => {
    sendViewToTealium({
      event_name: [EVENT_NAME.PAGE_VIEW],
      page_name: `${PAGE_NAME_PREFIX.ONBOARDING}${PAGE_NAME.UNDER_18}`,
      journey_name: journeyMap.get(journey)!,
      ...staticOnboardingTealiumVariables,
    });
  },

  onUnderageModalExitAction: (journey: Journey) => {
    sendLinkToTealium({
      event_name: [EVENT_NAME.UI_INTERACTION],
      link_page_name: `${PAGE_NAME_PREFIX.ONBOARDING}${PAGE_NAME.UNDER_18}`,
      journey_name: journeyMap.get(journey)!,
      link_id: `${LINK_PREFIX.SELF_SERVICE}${LINK_NAME.CLOSE_APPLICATION}`,
      ...staticOnboardingTealiumVariables,
    });
  },

  onUnderageModalEnd: (journey: Journey) => {
    sendLinkToTealium({
      event_name: [EVENT_NAME.UI_INTERACTION],
      link_page_name: `${PAGE_NAME_PREFIX.ONBOARDING}${PAGE_NAME.UNDER_18}`,
      journey_name: journeyMap.get(journey)!,
      link_id: `${LINK_PREFIX.SELF_SERVICE}${LINK_NAME.CLOSE_MODAL}`,
      ...staticOnboardingTealiumVariables,
    });
  },

  onHelpButtonActions: (
    pageName: PAGE_NAME,
    action: 'help' | 'call support number' | 'email support',
    journey: Journey,
  ) => {
    const linkMap = new Map([
      ['help', LINK_NAME.HELP],
      ['call support number', LINK_NAME.CALL_SUPPORT],
      ['email support', LINK_NAME.EMAIL_SUPPORT],
    ]);

    sendLinkToTealium({
      event_name: [EVENT_NAME.UI_INTERACTION, EVENT_NAME.SUPPORT_ISSUE_RAISED],
      link_page_name: `${PAGE_NAME_PREFIX.SELF_SERVICE}${pageName}`,
      link_name: `${LINK_PREFIX.SELF_SERVICE}${linkMap.get(action)!}`,
      journey_name: journeyMap.get(journey)!,
      ...staticOnboardingTealiumVariables,
    });
  },

  /** ####################
   *   Fee Slider - removed(SMME-4783)
   * ####################
   */

  onOpenFeesSliderClickAction: (journey: Journey) => {
    sendLinkToTealium({
      event_name: [EVENT_NAME.UI_INTERACTION],
      link_page_name: `${PAGE_NAME_PREFIX.ONBOARDING}${PAGE_NAME.ABOUT_YOUR_BUSINESS}`,
      journey_name: journeyMap.get(journey)!,
      link_name: `${LINK_PREFIX.SELF_SERVICE}${LINK_NAME.CALCULATE_YOUR_FEES}`,
      ...staticOnboardingTealiumVariables,
    });
  },

  onOpenFeesSlider: () => {
    sendViewToTealium({
      event_name: [EVENT_NAME.PAGE_VIEW],
      page_name: `${PAGE_NAME_PREFIX.ONBOARDING}${PAGE_NAME.CALCULATE_FEES}`,
      journey_name: JOURNEY_NAME.THIRD_PARTY,
      ...staticOnboardingTealiumVariables,
    });
  },

  OnCalculateFees: (deviceName: string, journey: Journey) => {
    sendLinkToTealium({
      event_name: [EVENT_NAME.UI_INTERACTION],
      link_page_name: `${PAGE_NAME_PREFIX.ONBOARDING}${PAGE_NAME.CALCULATE_FEES}`,
      journey_name: journeyMap.get(journey)!,
      link_name: `${LINK_PREFIX.SELF_SERVICE}${LINK_NAME.CALCULATE_YOUR_FEES}`,
      product_name: deviceName,
      ...staticOnboardingTealiumVariables,
    });
  },

  onMoreAboutCNPCP: (journey: Journey) => {
    sendLinkToTealium({
      link_page_name: `${PAGE_NAME_PREFIX.ONBOARDING}${PAGE_NAME.CALCULATE_FEES}`,
      event_name: [EVENT_NAME.UI_INTERACTION],
      journey_name: journeyMap.get(journey)!,
      link_name: `${LINK_PREFIX.SELF_SERVICE}${LINK_NAME.MORE_ABOUT_CP_CNP}`,
      ...staticOnboardingTealiumVariables,
    });
  },

  OnUseSlider: (price: number, journey: Journey) => {
    sendLinkToTealium({
      event_name: [EVENT_NAME.TOOL_START],
      journey_name: journeyMap.get(journey)!,
      link_page_name: `${PAGE_NAME_PREFIX.ONBOARDING}${PAGE_NAME.CALCULATE_FEES}`,
      link_name: `${LINK_PREFIX.SELF_SERVICE}${LINK_NAME.SELECT_TURNOVER_AMOUNT}`,
      tool_name: `${LINK_NAME}${LINK_NAME.SELECT_TURNOVER_AMOUNT}`,
      tool_input: `${price}`,
      ...staticOnboardingTealiumVariables,
    });
  },
};

export default OnboardingTealiumService;
